<template>
  <DpaIdOverlay ref="idOverlay" />
  <DpaNewsBaseModal
    ref="modal"
    :is-loading="isFetching"
    :submit-button-label="t('savedSearches.saveSearch')"
    tracking-place="notifySavedSearch"
    @submit="handleEditNotificationSaveSearch">
    <template #header>
      {{ t("control.notification") }}
    </template>
    <p class="text-sm/normal font-normal text-neutral-emphasis">
      {{ t("control.notificationText") }}
    </p>
    <div class="mt-4 space-y-4">
      <div class="flex items-center gap-1">
        <UiToggleButton
          v-model="_searchNotifyByApp"
          appearance="primary"
          label-position="right"
          label-text-size="sm"
          :size="isMobile ? 'sm' : 'lg'"
          @update:model-value="
            trackEvent(
              'notifySavedSearch:modal_click_toggleButton_notifyByIDApp',
              $event,
            )
          ">
          {{ t("control.appNotificationLabel") }}
        </UiToggleButton>
        <UiButton
          :aria-label="t('ariaLabels.openSavedSearchModal')"
          appearance="ghost"
          data-testid="dpa-id-app-info-btn"
          size="xs"
          @click="idOverlay?.showModal()">
          <UiIcon name="info" />
        </UiButton>
      </div>
      <UiToggleButton
        v-model="_searchNotifyByEmail"
        appearance="primary"
        label-position="right"
        :size="isMobile ? 'sm' : 'lg'"
        @update:model-value="
          trackEvent(
            'notifySavedSearch:modal_click_toggleButton_notifyByEMail',
            $event,
          )
        ">
        {{ t("control.notificationLabel") }}
      </UiToggleButton>
    </div>
  </DpaNewsBaseModal>
</template>

<script lang="ts" setup>
import UiButton from "@dpa-id-components/dpa-shared-components/src/components/UiButton/UiButton.vue";
import UiIcon from "@dpa-id-components/dpa-shared-components/src/components/UiIcon/UiIcon.vue";
import UiToggleButton from "@dpa-id-components/dpa-shared-components/src/components/UiToggleButton/UiToggleButton.vue";
import { computed, ref, toRef, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import DpaIdOverlay from "@/components/shared/DpaIdOverlay/DpaIdOverlay.vue";
import DpaNewsBaseModal from "@/components/shared/DpaNewsBaseModal/DpaNewsBaseModal.vue";
import { useEditNotificationSearch } from "@/composition/api/savedSearches.ts";
import useBreakpoints from "@/composition/shared/useBreakpoints.ts";
import { useTracking } from "@/composition/shared/useTracking.ts";
import type { SavedSearchItem } from "@/types/savedSearch";
import { handleLocalError } from "@/utils/errorHandling.ts";

const { t } = useI18n();
const { trackEvent } = useTracking();
const { isMobile } = useBreakpoints();

const props = defineProps<{
  searchId: string;
  searchNotifyByEmail: boolean;
  searchNotifyByApp: boolean;
}>();

const modal = useTemplateRef<InstanceType<typeof DpaNewsBaseModal>>("modal");
const idOverlay =
  useTemplateRef<InstanceType<typeof DpaIdOverlay>>("idOverlay");

const _searchNotifyByEmail = ref(props.searchNotifyByEmail);
const _searchNotifyByApp = ref(props.searchNotifyByApp);

const emit = defineEmits<{
  edited: [];
}>();

const searchId = toRef(props, "searchId");
const saveSearchItem = computed<Partial<SavedSearchItem>>(() => ({
  notificationSettings: {
    notifyByEmail: _searchNotifyByEmail.value,
    notifyByDpaIdPush: _searchNotifyByApp.value,
  },
}));
const {
  execute: editNotificationSearch,
  error: editSearchError,
  isFetching,
} = useEditNotificationSearch(searchId, saveSearchItem);

const handleEditNotificationSaveSearch = async () => {
  await editNotificationSearch();
  window.Appcues?.track?.("Saved Search Notification edited");

  if (editSearchError.value) {
    handleLocalError(
      editSearchError.value.status,
      t("errors.saveSearch.failed"),
    );
    return;
  }
  emit("edited");
  modal.value?.requestClose();
};

function showModal() {
  _searchNotifyByEmail.value = props.searchNotifyByEmail;
  _searchNotifyByApp.value = props.searchNotifyByApp;
  modal.value?.showModal();
}

function requestClose() {
  modal.value?.requestClose();
}

function close() {
  modal.value?.close();
}

defineExpose({ showModal, close, requestClose });
</script>
